import React, { useState } from 'react';

interface LoginProps {
  onLogin: (token: string) => void;
}

function Login({ onLogin }: LoginProps) {
  const [step, setStep] = useState<'credentials' | '2fa'>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCredentials = async () => {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Login failed');
      // Guest login: no 2FA required — token issued immediately
      if (!data.requires_2fa && data.access_token) {
        onLogin(data.access_token);
        return;
      }
      setStep('2fa');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || '2FA failed');
      localStorage.setItem('token', data.access_token);
      onLogin(data.access_token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-deep flex items-center justify-center">
      <div className="bg-surface rounded-card border border-border-base p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-5xl">📈</span>
          <h1 className="text-2xl font-bold mt-3 text-text-bright">TradingBot</h1>
          <p className="text-text-secondary text-sm mt-1">
            {step === 'credentials' ? 'Sign in to your account' : 'Two-factor authentication'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-status-red-soft border border-border-red text-status-red-muted px-4 py-3 rounded-btn mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Step 1 - Credentials */}
        {step === 'credentials' && (
          <div className="space-y-4">
            <div>
              <label className="text-text-secondary text-sm">Username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full bg-surface-2 border border-border-light rounded-btn px-3 py-2 mt-1 text-text-bright"
                placeholder="admin"
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
              />
            </div>
            <div>
              <label className="text-text-secondary text-sm">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-surface-2 border border-border-light rounded-btn px-3 py-2 mt-1 text-text-bright"
                placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
              />
            </div>
            <button
              onClick={handleCredentials}
              disabled={loading}
              className="w-full bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright py-2 rounded-btn font-medium mt-2"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        )}

        {/* Step 2 - 2FA */}
        {step === '2fa' && (
          <div className="space-y-4">
            <div className="bg-surface-2 rounded-btn p-4 text-sm text-text-primary text-center">
              Open Google Authenticator and enter the 6-digit code for TradingBot
            </div>
            <div>
              <label className="text-text-secondary text-sm">Authentication Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                className="w-full bg-surface-2 border border-border-light rounded-btn px-3 py-2 mt-1 text-text-bright text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                onKeyDown={e => e.key === 'Enter' && handle2FA()}
              />
            </div>
            <button
              onClick={handle2FA}
              disabled={loading}
              className="w-full bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright py-2 rounded-btn font-medium"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button
              onClick={() => setStep('credentials')}
              className="w-full text-text-secondary text-sm hover:text-text-bright"
            >
              ← Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
